import { createApi } from '@reduxjs/toolkit/query/react';
import { QualityAndRiskData } from '../../types/qualityAndRisk';
import { baseApiQuery } from '../basequery';
import { PaginationInfo } from '../../services/FormService';
import { GetQualityAndRiskResponse } from '../../types/qualityAndRisk';
import { formsApi } from './forms.api';

export const qualityAndRiskApi = createApi({
  reducerPath: 'qualityAndRiskApi',
  baseQuery: baseApiQuery,
  tagTypes: ['QualityAndRiskID'],
  endpoints: (builder) => ({
    /**
     * Fetches forms with optional filters, pagination, and search
     * @param params - { data: stringified JSON of filters, pagination, etc. }
     */
    getQualityAndRisk: builder.query<
      {
        data: {
          qualityRisks: QualityAndRiskData[];
          pagination: PaginationInfo;
        };
      },
      { params: Record<string, unknown> }
    >({
      query: ({ params }) => ({
        url: `/quality-risk`,
        method: 'GET',
        params,
      }),
    }),
    /**
     * Fetches a specific quality and risk form by ID
     * @param id - The ID of the quality and risk form
     */
    getQualityAndRiskById: builder.query<GetQualityAndRiskResponse, { id: number }>({
      query: ({ id }) => ({ url: `/quality-risk/${id}`, method: 'GET' }),
      providesTags: (_result, _error, { id }) => [{ type: 'QualityAndRiskID', id }],
    }),
    submitQualityAndRisk: builder.mutation<
      {
        data: {
          id: number;
          status: string;
          emailNotificationSent?: boolean;
        };
      },
      {
        id: number;
        data: {
          qualityRiskComments?: string | null;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/quality-risk/${id}/submit`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'QualityAndRiskID', id }],
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(formsApi.util.invalidateTags([{ type: 'MyFormsID', id }]));
        } catch {
          // Ignore rejected mutation results here so failed PATCH requests
          // do not cause unhandled promise rejections or cross-slice invalidations.
        }
      },
    }),
    returnToSubmitter: builder.mutation<
      {
        data: {
          id: number;
          status: string;
          emailNotificationSent?: boolean;
        };
      },
      {
        id: number;
        data: {
          qualityRiskComments: string;
        };
      }
    >({
      query: ({ id, data }) => ({
        url: `/quality-risk/${id}/returntosubmitter/submit`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'QualityAndRiskID', id }],
      onQueryStarted: async ({ id }, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(formsApi.util.invalidateTags([{ type: 'MyFormsID', id }]));
        } catch {
          // Ignore rejected mutation results here so failed PATCH requests
          // do not cause unhandled promise rejections or cross-slice invalidations.
        }
      },
    }),
  }),
});

export const {
  useLazyGetQualityAndRiskQuery,
  useGetQualityAndRiskQuery,
  useGetQualityAndRiskByIdQuery,
  useLazyGetQualityAndRiskByIdQuery,
  useSubmitQualityAndRiskMutation,
  useReturnToSubmitterMutation,
} = qualityAndRiskApi;
